import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import { ROUTES } from '../../constants';
import i18n from '../../utilities/i18n';
import SitePageHeader from './siteComponents/SiteHeader';
import { EntityType } from 'adc-sirius-module-common';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import { useQuery } from '@tanstack/react-query';
import {
  devicesBySiteQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
} from '../../queryOptions';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import { QrCodePopup } from '../qrCode/QrCodePopup';
import { siteCapabilitiesQueryOptions } from '../../queryOptions/capability';
import { SubjectsTable } from './siteComponents/SubjectsTable';

/**
 * Site component that displays the details of a specific site.
 * @returns {React.JSX.Element}
 */
export default function Site() {
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SUBJECTS.DETAIL.replace(':subjectKey', subjectKey),
      subjectLabel,
      site.data?.name || i18n.t('Page_Site.title'),
    );
  };

  const { siteKey } = useParams();

  const site = useQuery(siteQueryOptions({ key: siteKey }));

  const { data: capabilities } = useQuery(
    siteCapabilitiesQueryOptions({ key: siteKey }),
  );

  const errorMessage = useErrorMessageOnEntityView(
    site.error,
    'Page_Site.load_error',
  );

  const study = useQuery(studyQueryOptions({ key: site.data?.studyKey }));

  const devices = useQuery(
    devicesBySiteQueryOptions(
      {
        key: siteKey,
      },
      { enabled: !!siteKey },
    ),
  );

  const devicesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Device Name'), dataKey: 'appName' },
    { label: i18n.t('Entity.id'), dataKey: 'deviceId' },
    { label: i18n.t('Device.os'), dataKey: 'os' },
    { label: i18n.t('Device.model'), dataKey: 'phoneModel' },
  ];

  const qrCodeError = useMemo(() => {
    if (
      study.error ||
      site.error ||
      (study.isSuccess && !study.data) ||
      (site.isSuccess && !site.data)
    ) {
      return true;
    }

    return false;
  }, [
    study.error,
    study.isSuccess,
    study.data,
    site.error,
    site.isSuccess,
    site.data,
  ]);

  return (
    <VStack gap={'$4'} height={'100%'}>
      {site.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Site.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="site-error-banner"
        />
      ) : null}

      {site.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="site-loading" />
        </VStack>
      ) : null}

      {site.isSuccess ? (
        <VStack gap="$4">
          <SitePageHeader
            site={site.data}
            isReadonly={!capabilities?.canUpdateSite}
          >
            <QrCodePopup
              studyIdentifier={study.data?.name || ''}
              siteIdentifier={site.data?.name}
              studyKey={study.data?.key || ''}
              siteKey={site.data?.key}
              error={qrCodeError}
              loading={study.isPending || site.isPending}
            />
          </SitePageHeader>

          {capabilities?.canGetSubjects ? (
            <SubjectsTable
              siteKey={site.data.key}
              sites={[site.data]}
              navigateToSubject={navigateToSubject}
              canCreateSubject={capabilities?.canInsertSubject}
            />
          ) : null}

          <DynamicTable
            isLoading={devices.isPending}
            hasLoadingError={devices.isError}
            title={i18n.t('Page_Site.table_devices_title')}
            description={i18n.t('Page_Site.table_devices_description')}
            columnData={devicesTableColumnData}
            rowData={devices.data || []}
            toolbarRightContent={
              <AssignDeviceModal
                entityType={EntityType.StudySite}
                entityKey={site.data.key}
                entityName={site.data.name}
                trigger={
                  <Button
                    size="medium"
                    label={i18n.t('Page_Site.manage_assignments')}
                    onPress={() => {}}
                    disabled={devices.isPending}
                  />
                }
              />
            }
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
